import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  doc,
  orderBy,
  query,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
  ActivityType,
  ActivityVariableDefinition,
} from "../types/activity";
import { normalizeActivityVariables } from "../utils/unitUtils";

export class ActivityService {
  /**
   * Fetch user's activity types
   */
  static async fetchActivityTypes(userId: string): Promise<ActivityType[]> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    try {
      const typesRef = collection(db, "activityTypes", userId, "types");
      const q = query(typesRef, orderBy("name"));
      const snapshot = await getDocs(q);

      const activityTypes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ActivityType[];

      // Filter out soft-deleted activity types
      return activityTypes.filter((activityType) => !activityType.deleted);
    } catch (error) {
      console.error("Error fetching activity types:", error);
      throw new Error("Failed to fetch activity types");
    }
  }

    /**
   * Fetch user's activity types
   */
  static async fetchCoachActivityTypes(orgID: string): Promise<ActivityType[]> {
    if (!orgID) {
      throw new Error("Organisation ID is required");
    }

    try {
      const typesRef = collection(db, "organisations", orgID, "activityTypes");
      const q = query(typesRef, orderBy("name"));
      const snapshot = await getDocs(q);

      const activityTypes = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as ActivityType[];

      // Filter out soft-deleted activity types
      return activityTypes.filter((activityType) => !activityType.deleted);
    } catch (error) {
      console.error("Error fetching activity types:", error);
      throw new Error("Failed to fetch activity types");
    }
  }

  /**
   * Create a new activity type
   */
  static async createActivityType(
    userId: string,
    activityTypeData: {
      name: string;
      description: string;
      variables: (string | ActivityVariableDefinition)[]; // Support both formats
      image?: string;
    }
  ): Promise<string> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    // Validation
    if (!activityTypeData.name.trim()) {
      throw new Error("Activity type name is required");
    }

    if (
      !activityTypeData.variables ||
      activityTypeData.variables.length === 0
    ) {
      throw new Error("At least one variable is required");
    }

    try {
      // Normalize variables to new format (convert legacy strings to ActivityVariableDefinition)
      const normalizedVariables = normalizeActivityVariables(activityTypeData.variables);

      const dataToSave = {
        name: activityTypeData.name.trim(),
        description: activityTypeData.description.trim(),
        variables: normalizedVariables, // Always save in new format
        image: activityTypeData.image?.trim() || "",
        deleted: false, // Set to false for new activity types
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(
        collection(db, "activityTypes", userId, "types"),
        dataToSave
      );

      return docRef.id;
    } catch (error) {
      console.error("Error creating activity type:", error);
      throw new Error("Failed to create activity type");
    }
  }

  /**
   * Update an existing activity type
   */
  static async updateActivityType(
    userId: string,
    activityTypeId: string,
    activityTypeData: {
      name: string;
      description: string;
      variables: (string | ActivityVariableDefinition)[]; // Support both formats
      image?: string;
      createdAt?: Timestamp | Date;
    }
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!activityTypeId) {
      throw new Error("Activity type ID is required");
    }

    // Validation
    if (!activityTypeData.name.trim()) {
      throw new Error("Activity type name is required");
    }

    if (
      !activityTypeData.variables ||
      activityTypeData.variables.length === 0
    ) {
      throw new Error("At least one variable is required");
    }

    try {
      // Normalize variables to new format (convert legacy strings to ActivityVariableDefinition)
      const normalizedVariables = normalizeActivityVariables(activityTypeData.variables);

      const dataToUpdate = {
        name: activityTypeData.name.trim(),
        description: activityTypeData.description.trim(),
        variables: normalizedVariables, // Always save in new format
        image: activityTypeData.image?.trim() || "",
        createdAt:
          activityTypeData.createdAt instanceof Date
            ? Timestamp.fromDate(activityTypeData.createdAt)
            : activityTypeData.createdAt || Timestamp.now(),
        updatedAt: Timestamp.now(),
        // Note: deliberately not updating the 'deleted' field - it should only be modified by deleteActivityType
      };

      await updateDoc(
        doc(db, "activityTypes", userId, "types", activityTypeId),
        dataToUpdate
      );
    } catch (error) {
      console.error("Error updating activity type:", error);
      throw new Error("Failed to update activity type");
    }
  }

  /**
   * Soft delete an activity type (sets deleted flag to true)
   */
  static async deleteActivityType(
    userId: string,
    activityTypeId: string
  ): Promise<void> {
    if (!userId) {
      throw new Error("User ID is required");
    }

    if (!activityTypeId) {
      throw new Error("Activity type ID is required");
    }

    try {
      // Soft delete by setting deleted flag to true instead of actually deleting
      await updateDoc(
        doc(db, "activityTypes", userId, "types", activityTypeId),
        {
          deleted: true,
          updatedAt: Timestamp.now(),
        }
      );
    } catch (error) {
      console.error("Error soft deleting activity type:", error);
      throw new Error("Failed to delete activity type");
    }
  }

  /**
   * Create or update an activity type (convenience method)
   */
  static async saveActivityType(
    userId: string,
    activityTypeData: {
      id?: string;
      name: string;
      description: string;
      variables: (string | ActivityVariableDefinition)[]; // Support both formats
      image?: string;
      createdAt?: Timestamp | Date;
    }
  ): Promise<void> {
    if (activityTypeData.id) {
      // Update existing
      await this.updateActivityType(userId, activityTypeData.id, {
        name: activityTypeData.name,
        description: activityTypeData.description,
        variables: activityTypeData.variables,
        image: activityTypeData.image,
        createdAt: activityTypeData.createdAt,
      });
    } else {
      // Create new
      await this.createActivityType(userId, {
        name: activityTypeData.name,
        description: activityTypeData.description,
        variables: activityTypeData.variables,
        image: activityTypeData.image,
      });
    }
  }
}

// Export singleton instance for convenience
export const activityService = ActivityService;
